import { createRoot, hydrateRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { PostHogErrorBoundary } from "@posthog/react";
import "./index.css";

// Early Sentry init — if user has already consented in a previous session,
// capture startup errors before the app even renders
try {
  const raw = localStorage.getItem("cookie_consent_v1");
  if (raw) {
    const consent = JSON.parse(raw);
    if (consent.errorMonitoring) {
      const dsn =
        (import.meta as any).env?.VITE_SENTRY_DSN || "";
      if (dsn) {
        Sentry.init({
          dsn,
          environment: (import.meta as any).env?.MODE || "production",
          integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
              maskAllText: false,
              blockAllMedia: false,
            }),
          ],
          tracesSampleRate: 0.2,
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
        });
      }
    }
  }
} catch {
  // Consent check failed — Sentry will be initialized later via Analytics.tsx
}

const container = document.getElementById("root");

if (!container) {
  throw new Error("Root-Element '#root' wurde nicht gefunden.");
}

// Detect prerendered content from react-snap — use hydration to preserve SEO HTML
const isPrerendered = container.hasChildNodes();

// For error rendering we always need a createRoot instance (hydration doesn't support .render())
let errorRoot: ReturnType<typeof createRoot> | null = null;
const getErrorRoot = () => {
  if (!errorRoot) {
    // If container was prerendered, clear it before creating a fresh root for errors
    if (isPrerendered) container.innerHTML = "";
    errorRoot = createRoot(container);
  }
  return errorRoot;
};

const renderStartupError = (error: unknown) => {
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : "Unbekannter Fehler";

  console.error("Startup error:", error);

  // Report to Sentry if available
  try {
    Sentry.captureException(error instanceof Error ? error : new Error(message));
  } catch {
    // Sentry not initialized yet — fine
  }

  getErrorRoot().render(
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
      <div className="max-w-xl space-y-4 text-left">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-foreground/60">
          Startfehler
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">
          Die Anwendung konnte nicht geladen werden.
        </h1>
        <p className="text-sm leading-relaxed text-foreground/70">
          Bitte Seite neu laden. Wenn der Fehler bestehen bleibt, die unten stehende Meldung pruefen.
        </p>
        <pre className="overflow-x-auto rounded-2xl bg-foreground/5 p-4 text-xs leading-relaxed text-foreground/80">
          {message}
        </pre>
      </div>
    </main>,
  );
};

window.addEventListener("error", (event) => {
  const msg = event.error?.message || (typeof event.message === 'string' ? event.message : '');
  if (msg.includes('_leaflet_pos')) { event.preventDefault(); return; }
  renderStartupError(event.error ?? event.message);
});

window.addEventListener("unhandledrejection", (event) => {
  const msg = event.reason?.message || String(event.reason || '');
  if (msg.includes('_leaflet_pos')) { event.preventDefault(); return; }
  renderStartupError(event.reason);
});

import("./App.tsx")
  .then(({ default: App }) => {
    const appJsx = (
      <Sentry.ErrorBoundary
        fallback={({ error }) => {
          const message = error instanceof Error ? error.message : "Unbekannter Fehler";
          return (
            <main className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
              <div className="max-w-xl space-y-4 text-left">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-foreground/60">
                  Anwendungsfehler
                </p>
                <h1 className="text-3xl font-semibold tracking-tight">
                  Es ist ein Fehler aufgetreten.
                </h1>
                <p className="text-sm leading-relaxed text-foreground/70">
                  Bitte laden Sie die Seite neu. Wenn der Fehler bestehen bleibt,
                  kontaktieren Sie uns.
                </p>
                <pre className="overflow-x-auto rounded-2xl bg-foreground/5 p-4 text-xs leading-relaxed text-foreground/80">
                  {message}
                </pre>
                <button
                  onClick={() => window.location.reload()}
                  className="rounded-full bg-gray-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-gray-800"
                >
                  Seite neu laden
                </button>
              </div>
            </main>
          );
        }}
      >
        <PostHogErrorBoundary>
          <App />
        </PostHogErrorBoundary>
      </Sentry.ErrorBoundary>
    );

    if (isPrerendered) {
      hydrateRoot(container, appJsx);
    } else {
      createRoot(container).render(appJsx);
    }
  })
  .catch((error) => {
    renderStartupError(error);
  });
